import { motion } from 'framer-motion';
import { Shield, ChevronLeft } from 'lucide-react';
import { SOVEREIGN } from '../../../epc/utils/epcProjectVisual';

/**
 * Slate-gradient hero for the role editor. Follows the DashboardHero pattern:
 * dark slate gradient surface, single accent glow, hairline divider at bottom,
 * left-aligned brand tile + title block, right-aligned step indicator.
 *
 * Animations are entry-only — opacity ramp on mount. No infinite motion.
 */
export interface RoleEditorHeroProps {
  roleName: string;
  mode: 'create' | 'edit';
  currentStep: number;
  totalSteps: number;
  onBack: () => void;
}

export default function RoleEditorHero({
  roleName,
  mode,
  currentStep,
  totalSteps,
  onBack,
}: RoleEditorHeroProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="relative overflow-hidden rounded-2xl mb-6"
      style={{
        background: `linear-gradient(180deg, ${SOVEREIGN.paper} 0%, ${SOVEREIGN.canvas} 100%)`,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
        boxShadow: SOVEREIGN.cardShadow,
      }}
    >
      {/* subtle champagne glow */}
      <div
        className="absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl pointer-events-none"
        style={{ background: 'rgba(201,169,97,0.12)' }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(to right, transparent, ${SOVEREIGN.innerRule}, transparent)` }}
      />

      <div className="relative flex items-center justify-between gap-4 p-6">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onBack}
            aria-label="Close editor"
            className="rounded-xl p-2 transition-colors"
            style={{ background: `rgba(11,59,46,0.07)`, color: SOVEREIGN.bottle }}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div
            className="flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-lg"
            style={{ background: SOVEREIGN.bottle }}
          >
            <Shield className="h-6 w-6" />
          </div>
          <div>
            <div
              className="text-[11px] uppercase tracking-wider font-semibold"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              {mode === 'create' ? 'New role' : 'Edit role'}
            </div>
            <h1
              className="text-xl sm:text-2xl font-extrabold tracking-tight"
              style={{ color: SOVEREIGN.title }}
            >
              {roleName || 'Untitled role'}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center gap-2 rounded-full px-3 py-1.5 text-xs"
            style={{
              background: 'rgba(11,59,46,0.07)',
              color: SOVEREIGN.kpiCaption,
            }}
          >
            <span className="font-semibold" style={{ color: SOVEREIGN.bottle }}>Step {currentStep}</span>
            <span style={{ color: SOVEREIGN.kpiCaption }}>of {totalSteps}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
